import { useRef, useState } from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { stringToDate } from 'modules/core/lib/dates'

import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { LEAVE_UNITS_TYPES } from 'modules/timeoff/lib/leaveEmployees'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import LabourUnit from 'modules/timeoff/store/models/TimeoffPolicy/LabourUnit'

interface Props {
  form: LeaveForm
  employeeTimeoffPolicyId: string | undefined | null
}

export interface LabourUnitsResult {
  value: number
  label: string
  enableFrenchCalendarFlag: boolean
  isNatural: boolean
  finishOnNatural: string
  countSmRestDays: boolean
}

const useLabourUnits = ({ form, employeeTimeoffPolicyId }: Props): LabourUnitsResult => {
  const employeeId = form.get('employee_id').value

  const [labourUnitResult, setLabourUnitResult] = useState<LabourUnitsResult>({
    value: 0,
    label: '',
    enableFrenchCalendarFlag: false,
    isNatural: false,
    finishOnNatural: '',
    countSmRestDays: false,
  })

  // FIXME: consider using LabourUnit static fetch method
  const labourUnits = useRef(
    new LabourUnit({
      employee_id: employeeId,
      days: 0,
      used_units: {
        days: 0,
        hours: 0,
      },
      workable_units: {
        days: 0,
        hours: 0,
      },
      is_natural: false,
      finish_on_natural: '',
      enable_french_calendar_flag: false,
    })
  ).current

  const from = form.get('start_on').value
  const to = form.get('finish_on').value
  const halfDay = form.get('half_day').value
  const leaveTypeId = form.get('leave_type_id').value

  const startTime =
    !!form.get('start_time').value && form.isHourlyFormComplete ? form.get('start_time').value : ''

  const hoursAmount =
    !!form.get('start_time').value && form.isHourlyFormComplete ? form.hoursAmount() : undefined

  useEffect(() => {
    if (!labourUnits || !leaveTypeId || !employeeId) return

    if (labourUnits.attributes.employee_id !== employeeId) {
      labourUnits.set({
        employee_id: employeeId,
      })
    }

    const fetchLabourUnits = async () => {
      const fromDate = stringToDate(from, 'ui')
      const toDate = stringToDate(to, 'ui')
      const type = leaveTypes.get(leaveTypeId)
      const accrues = !!type && type.attributes.accrues

      const { success } = await labourUnits.updateInfo({
        from: fromDate,
        to: toDate,
        half_day: halfDay,
        start_time: startTime,
        hours_amount_in_cents: hoursAmount,
        leave_type_id: leaveTypeId,
        accrues,
      })

      if (success) {
        const isWorkable = type?.attributes.workable
        const isHourly = type?.isHourlyForEmployee(employeeTimeoffPolicyId)
        const allowance = type?.getAllowanceForEmployee(employeeTimeoffPolicyId)
        let count = 0
        let label = 'days'
        let units = []
        let enableFrenchCalendarFlag = false
        let isNatural = false
        let naturalDate = ''

        if (isWorkable) {
          units = labourUnits.attributes.workable_units
        } else {
          units = labourUnits.attributes.used_units
        }

        isNatural = type?.attributes.accrues && !!allowance && allowance.natural
        const countSmRestDays = !!allowance?.countSmRestDays
        naturalDate = isNatural || countSmRestDays ? labourUnits.attributes.finish_on_natural : ''

        if (isHourly) {
          count = units[LEAVE_UNITS_TYPES.hours] || 0
          label = LEAVE_UNITS_TYPES.hours
        } else {
          count = units[LEAVE_UNITS_TYPES.days] || 0
          label = LEAVE_UNITS_TYPES.days
          enableFrenchCalendarFlag = labourUnits.attributes.enable_french_calendar_flag
        }

        setLabourUnitResult({
          value: count,
          label: label,
          enableFrenchCalendarFlag,
          isNatural,
          finishOnNatural: naturalDate,
          countSmRestDays: countSmRestDays,
        })
      }
    }

    fetchLabourUnits()
  }, [
    labourUnits,
    setLabourUnitResult,
    employeeId,
    from,
    to,
    halfDay,
    leaveTypeId,
    startTime,
    hoursAmount,
    employeeTimeoffPolicyId,
  ])

  return labourUnitResult
}

export default useLabourUnits
